import {
  Avatar,
  Box,
  Checkbox,
  FormControlLabel,
  Grid2,
  Typography,
} from '@mui/material';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';

export default function FeaturesFilterGroup(props) {
  const {
    data,
    handlers,
  } = props;
  const { t } = useTranslation();
  const colsMd = 4;
  const colsLg = 3;

  const isAllColorsSelected = !data.selectedColors || data.selectedColors.length === 0;
  const handleSelectAllColors = () => {
    handlers.onChangeSelectedColors(null, []);
  };
  const handleSelectColor = (color) => () => {
    const colors = data.selectedColors ?? [];
    const newColors = colors.includes(color)
      ? colors.filter((c) => c !== color)
      : [...colors, color];

    handlers.onChangeSelectedColors(null, newColors);
  };

  return (
    <div>
      <Box mb={4}>
        <Typography variant="button">
          {t('components.vehiclefleet.SearchAndFilterBar.FeaturesFilterGroup.features')}
        </Typography>
        <Grid2 container spacing={1}>
          <Grid2 size={{ lg: colsLg, md: colsMd, xs: 12 }}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={data.navigationSystem ?? false}
                  color="primary"
                  onChange={handlers.onChangeNavigationSystem}
                />
              )}
              label={t('components.vehiclefleet.SearchAndFilterBar.FeaturesFilterGroup.navigationSystem')}
            />
          </Grid2>
          <Grid2 size={{ lg: colsLg, md: colsMd, xs: 12 }}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={data.parkingSensors ?? false}
                  color="primary"
                  onChange={handlers.onChangeParkingSensors}
                />
              )}
              label={t('components.vehiclefleet.SearchAndFilterBar.FeaturesFilterGroup.parkingAssistance')}
            />
          </Grid2>
          <Grid2 size={{ lg: colsLg, md: colsMd, xs: 12 }}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={data.airConditioner ?? false}
                  color="primary"
                  onChange={handlers.onChangeAirConditioner}
                />
              )}
              label={t('components.vehiclefleet.SearchAndFilterBar.FeaturesFilterGroup.airConditioning')}
            />
          </Grid2>
          <Grid2 size={{ lg: colsLg, md: colsMd, xs: 12 }}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={data.seatHeating ?? false}
                  color="primary"
                  onChange={handlers.onChangeSeatHeating}
                />
              )}
              label={t('components.vehiclefleet.SearchAndFilterBar.FeaturesFilterGroup.seatHeating')}
            />
          </Grid2>
          <Grid2 size={{ lg: colsLg, md: colsMd, xs: 12 }}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={data.sunroof ?? false}
                  color="primary"
                  onChange={handlers.onChangeSunroof}
                />
              )}
              label={t('components.vehiclefleet.SearchAndFilterBar.FeaturesFilterGroup.slidingRoof')}
            />
          </Grid2>
          <Grid2 size={{ lg: colsLg, md: colsMd, xs: 12 }}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={data.cruiseControl ?? false}
                  color="primary"
                  onChange={handlers.onChangeCruiseControl}
                />
              )}
              label={t('components.vehiclefleet.SearchAndFilterBar.FeaturesFilterGroup.cruiseControl')}
            />
          </Grid2>
          <Grid2 size={{ lg: colsLg, md: colsMd, xs: 12 }}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={data.handsFreeKit ?? false}
                  color="primary"
                  onChange={handlers.onChangeHandsFreeKit}
                />
              )}
              label={t('components.vehiclefleet.SearchAndFilterBar.FeaturesFilterGroup.handsFreeSystem')}
            />
          </Grid2>
        </Grid2>
      </Box>
      <Box>
        <Typography variant="button">
          {t('components.vehiclefleet.SearchAndFilterBar.FeaturesFilterGroup.color')}
        </Typography>
        <Grid2 container spacing={1}>
          <Grid2 size={{ xs: 12 }}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={isAllColorsSelected}
                  color="primary"
                  onChange={handleSelectAllColors}
                />
              )}
              label={t('components.vehiclefleet.SearchAndFilterBar.FeaturesFilterGroup.allColors')}
            />
          </Grid2>
          {COLORS.map((item) => (
            <Grid2
              key={item.value}
              size={{ lg: colsLg, md: colsMd, xs: 12 }}
            >
              <Box
                alignItems="center"
                display="flex"
              >
                <Box mr={1}>
                  <Avatar
                    sx={{
                      backgroundColor: item.color,
                      width: 20,
                      height: 20,
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: 'rgba(0,0,0,0.1)',
                    }}
                  >
                    {' '}
                  </Avatar>
                </Box>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={(data.selectedColors ?? []).includes(item.value)}
                      color="primary"
                      onChange={handleSelectColor(item.value)}
                    />
                  )}
                  label={t(`components.vehiclefleet.SearchAndFilterBar.FeaturesFilterGroup.colors.${item.value}`)}
                />
              </Box>
            </Grid2>
          ))}
        </Grid2>
      </Box>
    </div>
  );
}

FeaturesFilterGroup.propTypes = {
  data: PropTypes.object.isRequired,
  handlers: PropTypes.object.isRequired,
};

const COLORS = [
  {
    value: 'black',
    color: '#000000',
  },
  {
    value: 'anthracite',
    color: '#424242',
  },
  {
    value: 'gray',
    color: '#9E9E9E',
  },
  {
    value: 'silver',
    color: '#EEEEEE',
  },
  {
    value: 'white',
    color: '#FFFFFF',
  },
  {
    value: 'blue',
    color: '#1565C0',
  },
  {
    value: 'green',
    color: '#2E7D32',
  },
  {
    value: 'yellow',
    color: '#FFEB3B',
  },
  {
    value: 'gold',
    color: '#F9A825',
  },
  {
    value: 'orange',
    color: '#FF5722',
  },
  {
    value: 'red',
    color: '#C62828',
  },
  {
    value: 'violet',
    color: '#4527A0',
  },
  {
    value: 'beige',
    color: '#d1bc8a',
  },
  {
    value: 'bronze',
    color: '#cc8e34',
  },
  {
    value: 'brown',
    color: '#5D4037',
  },
];
